import { MutationCache, QueryClient, QueryKey, notifyManager } from '@tanstack/react-query';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { invalidates?: QueryKey };
  }
}

// the default scheduler notifies on a `setTimeout(0)`, which paints an optimistic update one frame late
notifyManager.setScheduler((notify) => notify());

export const queryClient = new QueryClient({
  // a mutation stays pending until the queries it invalidates hold data fetched after it, wherever they are on screen
  mutationCache: new MutationCache({
    onSettled: (_data, _error, _variables, _context, mutation) =>
      mutation.meta?.invalidates && refreshQueries(mutation.meta.invalidates),
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
    },
  },
});

/** Resolves once every query of `queryKey` on screen holds data fetched after the call */
export async function refreshQueries(queryKey: QueryKey) {
  const loading = { queryKey, type: 'active', fetchStatus: 'fetching' } as const;

  // a first load requested before the write would answer without it, and invalidating doesn't restart one
  await queryClient.cancelQueries({ queryKey, predicate: (query) => query.state.data === undefined });
  await queryClient.invalidateQueries({ queryKey });
  // a query mounted meanwhile, such as a span scrolled to, loads without the invalidation waiting for it
  while (queryClient.isFetching(loading)) {
    await queryClient.refetchQueries(loading, { cancelRefetch: false });
  }
}
